/**
 * Dimension Extractor Service
 * Extracts dimensions from Etsy listing descriptions with various syntax patterns
 */

class DimensionExtractor {
  /**
   * Extract dimensions from listing description
   */
  extractDimensionsFromDescription(description) {
    if (!description) return {};

    const dimensions = {};

    // Helper: normalize size names (MED -> MEDIUM, etc.)
    const normalizeSizeKey = (name) => {
      const up = String(name || '')
        .trim()
        .toUpperCase();
      const map = {
        MED: 'MEDIUM',
        MEDIUM: 'MEDIUM',
        LAR: 'LARGE',
        LARGE: 'LARGE',
        JUM: 'JUMBO',
        JUMBO: 'JUMBO',
      };
      return map[up] || up;
    };

    // Decode HTML entities first
    const decodedDescription = description
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ');

    // Pattern 1: "ATOM: 0.8"w x 0.4"h approx."
    const pattern1 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx\.?/gi;
    let match;
    while ((match = pattern1.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 2: "ATOM: 0.8"w x 0.4"h approximately."
    const pattern2 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h\s*approximately\.?/gi;
    while ((match = pattern2.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 3: "ATOM: 0.8" w x 0.4"h approx." (with space before w)
    const pattern3 = /(\w+):\s*([\d.]+)"\s*w\s*x\s*([\d.]+)"h\s*approx\.?/gi;
    while ((match = pattern3.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 4: "Stitch is 0.8"w x 0.8" h approximately." (character-specific)
    const pattern4 = /(\w+)\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"\s*h\s*approx\.?/gi;
    while ((match = pattern4.exec(decodedDescription)) !== null) {
      const [, character, width, height] = match;
      // For character-specific dimensions, we'll use the character name as the key
      dimensions[normalizeSizeKey(character)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
        character: character,
      };
    }

    // Pattern 5: "SMALL is 8.4"w x 4.0"h approx."
    const pattern5 = /(\w+)\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx\.?/gi;
    while ((match = pattern5.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 6: "SMALL is 8.4"w x 4.0"h approx." (with space before h)
    const pattern6 = /(\w+)\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"\s*h\s*approx\.?/gi;
    while ((match = pattern6.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 7: "SMALL is 8.4"w x 4.0"h approx." (no space before h)
    const pattern7 = /(\w+)\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx\.?/gi;
    while ((match = pattern7.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 8: "MICRO are 0.9"w x 0.7"h approximately each"
    const pattern8 = /(\w+)\s+are\s+([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx\.?/gi;
    while ((match = pattern8.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 9: "MICRO are 0.9"w x 0.7"h approximately each" (with space before h)
    const pattern9 = /(\w+)\s+are\s+([\d.]+)"w\s*x\s*([\d.]+)"\s*h\s*approx\.?/gi;
    while ((match = pattern9.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 10: "SMALL: 1.2"w x 2.2"h" (no approx/approximately)
    const pattern10 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h(?:\s|$)/gim;
    while ((match = pattern10.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 11: "MICRO: 0.8"h x 1.0"w approx." (height first, then width)
    const pattern11 = /(\w+):\s*([\d.]+)"h\s*x\s*([\d.]+)"w\s*approx\.?/gi;
    while ((match = pattern11.exec(decodedDescription)) !== null) {
      const [, sizeName, height, width] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 12: "MICRO: 0.8"h x 1.0"w" (height first, then width, no approx)
    const pattern12 = /(\w+):\s*([\d.]+)"h\s*x\s*([\d.]+)"w(?:\s|$)/gim;
    while ((match = pattern12.exec(decodedDescription)) !== null) {
      const [, sizeName, height, width] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 13: "NANO: 2.2"w x 0.8"h you get 2 at this size!" (with additional text after dimensions)
    const pattern13 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h[^"]*(?:\s|$)/gim;
    while ((match = pattern13.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 14: "SMALL: 6.9"w x 2.4"h." (with period at end)
    const pattern14 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h\.?(?:\s|$)/gim;
    while ((match = pattern14.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 15: "Small: 2.5" x 2.5" approximately." (square dimensions, no w/h)
    const pattern15 = /(\w+):\s*([\d.]+)"\s*x\s*([\d.]+)"\s*approx\.?/gim;
    while ((match = pattern15.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[sizeName.toUpperCase()] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 16: "Vehicle Only is 1.9"w x 0.7"h approximately." (character-specific with "is")
    const pattern16 = /(\w+(?:\s+\w+)*)\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx\.?/gim;
    while ((match = pattern16.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[sizeName.toUpperCase()] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 17: "Corner Dirt is 4.0"w x 1.0"h approx" (character-specific with "is", no period)
    const pattern17 = /(\w+(?:\s+\w+)*)\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx(?:\s|$)/gim;
    while ((match = pattern17.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[sizeName.toUpperCase()] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 18: "Behind Mirror Decal is 5.0"w x 1.6"h" (character-specific with "is", no approx)
    const pattern18 = /(\w+(?:\s+\w+)*)\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"h(?:\s|$)/gim;
    while ((match = pattern18.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[sizeName.toUpperCase()] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 19: "MICRO: 1.0"w x 0.5"h approximately each." (with "each" at end)
    const pattern19 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx\.?\w*(?:\s|$)/gim;
    while ((match = pattern19.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[sizeName.toUpperCase()] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 20: "SMALL: 1.5"w x 1.0"h each" (with "each" at end, no approx)
    const pattern20 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h\s*each(?:\s|$)/gim;
    while ((match = pattern20.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[sizeName.toUpperCase()] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 21: "MEDIUM: 2.7"w x 1.3"h each" (with "each" at end, no approx)
    const pattern21 = /(\w+):\s*([\d.]+)"w\s*x\s*([\d.]+)"h\s*each(?:\s|$)/gim;
    while ((match = pattern21.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[sizeName.toUpperCase()] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 22: "Size is 3.6"w" x 1.0" high approximately" (with "high" instead of "h")
    const pattern22 = /Size\s+is\s+([\d.]+)"w\s*x\s*([\d.]+)"\s*(?:h|height|high)\s*approx\.?/gim;
    while ((match = pattern22.exec(decodedDescription)) !== null) {
      const [, width, height] = match;
      dimensions['DEFAULT'] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 23: "MICRO: Standing 0.9"w x 0.8"h approx." (with character description)
    const pattern23 = /(\w+):\s+\w+\s+([\d.]+)"w\s*x\s*([\d.]+)"h\s*approx\.?/gim;
    while ((match = pattern23.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 24: "Size is approximately 2.8'w x 1.7"h" (with single quote and "approximately")
    const pattern24 = /Size\s+is\s+approx\.?\s*([\d.]+)'w\s*x\s*([\d.]+)"(?:h|height|high)/gim;
    while ((match = pattern24.exec(decodedDescription)) !== null) {
      const [, width, height] = match;
      dimensions['DEFAULT'] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Pattern 25: "SIZE: 0.4" wide x 1.7" high approx."
    const pattern25 =
      /(\w+):\s*([\d.]+)"\s*(?:w|width|wide)\s*x\s*([\d.]+)"\s*(?:h|height|high)\s*approx\.?/gim;
    while ((match = pattern25.exec(decodedDescription)) !== null) {
      const [, sizeName, width, height] = match;
      dimensions[normalizeSizeKey(sizeName)] = {
        width: parseFloat(width),
        height: parseFloat(height),
        unit: 'inches',
        source: 'description',
      };
    }

    // Normalize keys
    const normalized = {};
    Object.keys(dimensions).forEach((k) => {
      const key = normalizeSizeKey(k);
      normalized[key] = dimensions[k];
    });
    return normalized;
  }

  /**
   * Extract dimensions from size variations (fallback)
   */
  extractDimensionsFromVariations(extractedVariations) {
    if (!extractedVariations?.sizes) {
      return {};
    }

    const dimensions = {};

    extractedVariations.sizes.forEach((sizeString) => {
      // Extract dimensions from strings like "ATOM 0.8w x 0.4h inches"
      const match = sizeString.match(/(\w+)\s+([\d.]+)w\s+x\s+([\d.]+)h\s+inches/);
      if (match) {
        const [, sizeName, width, height] = match;
        dimensions[sizeName] = {
          width: parseFloat(width),
          height: parseFloat(height),
          unit: 'inches',
          source: 'variations',
        };
      }
    });

    return dimensions;
  }

  /**
   * Combine dimensions from multiple sources (description takes priority)
   */
  combineDimensions(descriptionDimensions, variationDimensions) {
    const combined = { ...variationDimensions };

    // Description dimensions take priority
    Object.keys(descriptionDimensions).forEach((key) => {
      combined[key] = descriptionDimensions[key];
    });

    return combined;
  }

  /**
   * Extract all dimensions from a listing
   */
  extractAllDimensions(listingData) {
    const descriptionDimensions = this.extractDimensionsFromDescription(
      listingData.listing?.description || ''
    );

    const variationDimensions = this.extractDimensionsFromVariations(
      listingData.extractedVariations
    );

    return this.combineDimensions(descriptionDimensions, variationDimensions);
  }

  /**
   * Format dimensions for display
   */
  formatDimensions(dimensions) {
    const formatted = {};

    Object.keys(dimensions).forEach((key) => {
      const dim = dimensions[key];
      formatted[key] = {
        ...dim,
        display: `${dim.width}"w x ${dim.height}"h`,
        source: dim.source || 'unknown',
      };
    });

    return formatted;
  }

  /**
   * Get dimensions summary for logging
   */
  getDimensionsSummary(dimensions) {
    const summary = [];

    Object.keys(dimensions).forEach((key) => {
      const dim = dimensions[key];
      const source = dim.source || 'unknown';
      summary.push(`${key}: ${dim.width}"w x ${dim.height}"h (${source})`);
    });

    return summary;
  }
}

export default new DimensionExtractor();
